import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { CircularProgress } from '@mui/material';
import useUsers from 'api/useUsers';
import { GamePortalPageModel } from 'common/contracts';
import { TabsPanel } from 'design-system/TabPanel/TabPanel';
import { closeModal, openModal } from 'store/portalModalDialogSlice';
import { z } from 'zod';

import { usePages } from '../../../api/portal/usePagesApi';
import { PORTAL_MODAL_NAMES } from '../../../components/DialogModalManager/constants/modalConfigs';
import FormHeader from '../../../components/FormHeader/FormHeader';
import { Form } from '../../../components/ui';
import { EToasterStatus, showToast } from '../../../utils/showToast';
import { PagesFormProps } from '../types';

import {
  EBlockType,
  EPagesRadioButtonOptions,
  EPageState,
  EPagesTemplateOptions,
  PAGES_RADIO_BUTTON_OPTIONS
} from './constants';
import { defaultPageFormValues } from './defaults';
import { ManageBlocksForm } from './ManageBlocksTab/ManageBlocksForm';
import { SettingsForm } from './SettingsTab/SettingsForm';
import { pageFormSchema } from './validation-schema';

export const PagesForm: React.FC<PagesFormProps> = ({
  edit = false,
  dup = false
}) => {
  const [tab, setTab] = useState<EPageState>(EPageState.SETTINGS);

  const { pageId } = useParams();
  const navigate = useNavigate();
  const { addPage, updatePage, getPage, getPages } = usePages(pageId);

  const { publisherId } = useParams();
  const { fetchFeatureFlags } = useUsers({ publisherId });
  const featureFlags = useMemo(
    () => fetchFeatureFlags.data?.featureFlags,
    [fetchFeatureFlags.data?.featureFlags]
  );

  const isManageBlocksEnabled = useMemo(
    () =>
      featureFlags?.dashboard_game_portal_mange_blocks &&
      getPage.data?.pageTemplate !== EPagesTemplateOptions.STORE,
    [
      featureFlags?.dashboard_game_portal_mange_blocks,
      getPage.data?.pageTemplate
    ]
  );

  const dispatch = useDispatch();

  const BUTTON_TEXT = edit ? 'Update' : 'Save';

  const form = useForm<z.infer<typeof pageFormSchema>>({
    resolver: zodResolver(pageFormSchema),
    mode: 'onChange',
    defaultValues: defaultPageFormValues
  });

  const {
    formState: { isValid, isDirty }
  } = form;

  const initializeForm = useCallback(
    (data: Partial<GamePortalPageModel>) => {
      let isPublishDateEnabled = false;
      let state = data.state;

      if (
        data.state === EPagesRadioButtonOptions.SCHEDULED &&
        data.publishDate
      ) {
        isPublishDateEnabled = true;
        state = EPagesRadioButtonOptions.HIDDEN;
      }

      const {
        priority: dataPriority,
        subPagePriority: dataSubPagePriority,
        ...restData
      } = data;

      const priority = data.isSubPage ? undefined : dataPriority;
      const subPagePriority = data.isSubPage
        ? dataSubPagePriority ?? dataPriority
        : undefined;

      form.reset({
        ...defaultPageFormValues,
        ...restData,
        state,
        isPublishDateEnabled,
        parentPageId:
          data.parentPageId === null ? undefined : data.parentPageId,
        ...(priority !== undefined && { priority }),
        ...(subPagePriority !== undefined && { subPagePriority })
      });

      if (isPublishDateEnabled !== form.getValues('isPublishDateEnabled')) {
        form.setValue('isPublishDateEnabled', isPublishDateEnabled);
      }
    },
    [form]
  );

  useEffect(() => {
    if (edit && getPage.data) {
      initializeForm(getPage.data);
    } else if (dup && getPage.data) {
      initializeForm({
        ...getPage.data,
        pageName: `${getPage.data.pageName}_duplicated`
      });
    }
  }, [edit, dup, getPage.data, form, initializeForm]);

  const isUpdateButtonEnabled = useMemo(() => {
    if (dup) return isValid;
    return isValid && isDirty;
  }, [dup, isValid, isDirty]);

  const onCloseForm = () => {
    navigate('../');
  };

  const prepareSubmitData = (data: z.infer<typeof pageFormSchema>) => {
    const { isPublishDateEnabled, ...submitData } = data;
    if (submitData.state === EPagesRadioButtonOptions.VISIBLE) {
      delete submitData.publishDate;
    } else if (submitData.state === EPagesRadioButtonOptions.HIDDEN) {
      if (isPublishDateEnabled && submitData.publishDate) {
        submitData.state = EPagesRadioButtonOptions.SCHEDULED;
      } else {
        delete submitData.publishDate;
      }
    }

    if (!submitData.isSubPage) {
      submitData.parentPageId = null;
      submitData.subPagePriority = null;
      submitData.priority = submitData.priority ?? null;
    } else {
      if (submitData.parentPageId === undefined) {
        submitData.parentPageId = null;
      }
      submitData.priority = null;
      submitData.subPagePriority = submitData.subPagePriority ?? null;
    }

    if (!isManageBlocksEnabled && 'blocksV2' in submitData) {
      delete submitData.blocksV2;
    }

    if (submitData.blocksV2 && submitData.blocksV2.length > 0) {
      submitData.blocksV2 = submitData.blocksV2.map((block) => {
        if (block.type === EBlockType.CTA && block.items) {
          const items = block.items.map((item) => {
            if (item.type === EBlockType.CTA) {
              if (item.text === '') {
                delete item.text;
                return item
              }
              return item;
            }

            return item;
          });
          return {
            ...block,
            items: items
          }
        }

        if (block.type === EBlockType.DAILY_BONUS) {
          if (block.redirectUrl === '') {
            delete block.redirectUrl;
          }
          return block;
        }

        return block;
      })
    }

    return submitData;
  };

  const onSubmit = async (data: z.infer<typeof pageFormSchema>) => {
    const submitData = prepareSubmitData(data);

    const onSuccess = () => {
      showToast({
        message: edit ? 'Page updated successfully' : 'Page added successfully',
        status: EToasterStatus.SUCCESS
      });
      getPages.refetch();
      onCloseForm();
    };

    const onError = () => {
      showToast({
        message: edit ? 'Error updating page' : 'Error adding page',
        status: EToasterStatus.ERROR
      });
    };

    if (edit && pageId && !dup) {
      updatePage.mutate({ form: submitData, pageId }, { onSuccess, onError });
    } else {
      addPage.mutate(submitData, { onSuccess, onError });
    }
  };

  const handleFormSubmit = () => {
    form.handleSubmit(onSubmit)();
  };

  const handleOptionChange = (option: EPagesRadioButtonOptions) => {
    dispatch(
      openModal({
        currentModal: PORTAL_MODAL_NAMES.PUBLISH_PAGE,
        modalActions: {
          onConfirm: {
            label: 'Save changes',
            action: () => {
              form.setValue('state', option, {
                shouldDirty: true,
                shouldValidate: true
              });
              if (option === EPagesRadioButtonOptions.HIDDEN) {
                form.setValue('publishDate', '', {
                  shouldDirty: true,
                  shouldValidate: true
                });
              }

              if (option === EPagesRadioButtonOptions.VISIBLE) {
                form.setValue('publishDate', '', {
                  shouldDirty: true,
                  shouldValidate: true
                });
                form.setValue('isPublishDateEnabled', false, {
                  shouldDirty: true,
                  shouldValidate: true
                });
              }
              dispatch(closeModal());
            }
          },
          onClose: {
            label: 'Not Now',
            action: () => {
              dispatch(closeModal());
            },
            variant: 'link'
          }
        }
      })
    );
  };

  const tabs = useMemo(() => {
    return [
      {
        label: 'Settings',
        value: EPageState.SETTINGS
      },
      {
        label: 'Manage Blocks',
        value: EPageState.MANAGE_BLOCKS,
        shouldHide: !isManageBlocksEnabled
      }
    ];
  }, [isManageBlocksEnabled]);

  return (
    <div
      className="bg-white fixed h-screen w-full top-0 left-0 z-[1000] overflow-scroll"
      data-testid="PagesForm-Wrapper"
    >
      {(edit || dup) && getPage.isLoading && (
        <div
          className="absolute w-full h-full top-0 left-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm"
          data-testid="PagesForm-Loading"
        >
          <CircularProgress />
        </div>
      )}
      <FormHeader
        dataTestId="PagesForm-Header"
        title={`${edit ? 'Edit' : 'Create'} Portal Page`}
        mainButtonText={BUTTON_TEXT}
        onCloseForm={onCloseForm}
        onMainButtonClick={handleFormSubmit}
        closeFormOnSubmit={false}
        onOptionChange={(option) =>
          handleOptionChange(option as EPagesRadioButtonOptions)
        }
        selectedOption={form.watch('state')}
        options={PAGES_RADIO_BUTTON_OPTIONS}
        isFormValid={isUpdateButtonEnabled}
      />
      <div className="mt-7 mb-9 w-[528px] mx-auto">
        <TabsPanel
          tabs={tabs}
          handleTabChange={(event: any, newValue: EPageState) =>
            setTab(newValue)
          }
          activeTabVal={tab}
          padding={false}
        />
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} data-testid="PagesForm">
            {tab === EPageState.SETTINGS && (
              <SettingsForm edit={edit} dup={dup} />
            )}
            {tab === EPageState.MANAGE_BLOCKS && (
              <ManageBlocksForm edit={edit} dup={dup} />
            )}
          </form>
        </Form>
      </div>
    </div>
  );
};
